import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MSidebar from "../../components/ui/MSidebar";
import UserProfile from './UserProfile';
import "../../styles/manage.css";
import Dashboard from './Dashboard';
import NotificationModal from './NotificationModal';
import BotControl from './BotControl';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

// 页面组件
const _Dashboard = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    <Dashboard></Dashboard>
  </motion.div>
);

const _BotControl = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    <BotControl></BotControl>
  </motion.div>
);

const _UserProfile = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    <UserProfile />
  </motion.div>
);

const _ResourceLibrary = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    <h1>资料库页面</h1>
    <p>这里是资料库的内容...</p>
  </motion.div>
);

// 页面配置
const pages = [
  { id: 'dashboard', title: '仪表板', component: _Dashboard },
  { id: 'botcontrol', title: '假人控制', component: _BotControl },
  { id: 'profile', title: '个人中心', component: _UserProfile },
  { id: 'resource', title: '资料库', component: _ResourceLibrary }
];

function Manager(props) {
  useDocumentTitle("天地源 | 控制面板");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [notification, setNotification] = useState({
    isOpen: false,
    message: '',
    type: 'info'
  });
  
  // 添加健康检查相关的状态
  const [isServerHealthy, setIsServerHealthy] = useState(true);
  
  // 添加健康检查相关的 ref
  const healthCheckIntervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  
  const CurrentComponent = pages[currentPageIndex].component;
  
  // 显示通知的函数
  const showNotification = (message, type = 'info', duration = 3000) => {
    setNotification({
      isOpen: true,
      message,
      type,
      duration
    });
  };
  
  // 关闭通知
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };
  
  // 健康检查函数
  const performHealthCheck = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch('https://api2.ororigin.xyz/api/health', {
        method: 'GET',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setIsServerHealthy(true);
        // 如果之前有失败的通知，关闭它
        if (!isServerHealthy) {
          closeNotification();
        }
        // 成功后，继续正常间隔检查（例如每30秒一次）
        scheduleNextHealthCheck(1200000);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      setIsServerHealthy(false);
      // 显示连接失败通知2秒
      showNotification('无法连接到服务器', 'error', 4000);
      
      // 2秒后关闭通知，然后等待5秒重试
      setTimeout(() => {
        scheduleNextHealthCheck(5000);
      }, 4000);
    }
  };
  
  // 安排下一次健康检查
  const scheduleNextHealthCheck = (delay) => {
    // 清除现有的定时器
    if (healthCheckIntervalRef.current) {
      clearTimeout(healthCheckIntervalRef.current);
    }
    
    healthCheckIntervalRef.current = setTimeout(() => {
      performHealthCheck();
    }, delay);
  };
  
  // 更新 URL hash
  const updateHash = (index) => {
    if(pages[index].id){
      window.location.hash = `#${pages[index].id}`;
    }else{
      window.location.assign("https://tdy.ororigin.xyz");
    }
  };
  
  // 处理侧边栏选项变化
  const handleSidebarChange = (index) => {
    setCurrentPageIndex(index);
    updateHash(index);
  };
  
  // 初始化健康检查
  useEffect(() => {
    // 组件挂载后立即执行一次健康检查
    performHealthCheck();
    
    // 清理函数
    return () => {
      if (healthCheckIntervalRef.current) {
        clearTimeout(healthCheckIntervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);
  
  // 监听 hash 变化
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const pageIndex = pages.findIndex(page => page.id === hash);
        if (pageIndex >= 0 && pageIndex !== currentPageIndex) {
          setCurrentPageIndex(pageIndex);
        }
      }
    };
    
    // 初始检查
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const pageIndex = pages.findIndex(page => page.id === hash);
      if (pageIndex >= 0) {
        setCurrentPageIndex(pageIndex);
      }
    }
    
    // 监听 hash 变化
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [currentPageIndex]);
  
  return (
    <div className="manage">
      {/* 添加通知模态框到最外层 */}
      <NotificationModal
        isOpen={notification.isOpen}
        message={notification.message}
        type={notification.type}
        duration={notification.duration}
        onClose={closeNotification}
      />
      
      <MSidebar 
        selectedIndex={currentPageIndex} 
        onOptionChange={handleSidebarChange} 
      />
      <div className='manage-bg'></div>
      <div className="manage-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPageIndex}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ width: '100%', height: '100%' }}
          >
            <CurrentComponent/>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default Manager;